import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  EntityCard,
  EntityCardActions,
  EntityCardBody,
  EntityCardFooter,
  EntityCardHeader,
  EntityCardRow,
  Input,
  Select,
  Table,
  Tabs,
  Textarea,
  cn,
  confirmDialog,
  type TableColumn,
  type Tone,
} from "../../../components/ui";
import Modal from "../../../components/base/Modal";
import {
  exchangeApi,
  apiErrorMessage,
  type DocStatus,
  type Load,
  type Match,
  type Me,
  type Offer,
  type PricingMode,
  type Profile,
  type SuggestedRate,
  type Trip,
  type TrustLevel,
  type UnitType,
} from "../exchange/api";
import {
  PRICING_LABELS,
  TRUST_META,
  TRUST_ORDER,
  UNIT_ICONS,
  UNIT_LABELS,
  fmtDateTime,
  fmtDay,
  fmtWindow,
  fromLocalInput,
  money,
  pct,
  relative,
  toDate,
  toLocalInput,
} from "../exchange/format";
import { useAsync, useMe } from "../exchange/useMe";
import {
  CM_BASE,
  CitySelect,
  ErrorState,
  GateNotice,
  Kpi,
  Loader,
  RouteLine,
  Section,
  TripDetailModal,
  TripList,
  TripStatusBadge,
  TrustBadge,
} from "../exchange/components";

// ===========================================================================
// Cargo Market — espacio del EMBARCADOR (comprador de transporte).
// Todo sale del backend real (/cargoExchange/*). El embarcador siempre ve el
// TOTAL (flete + comisión WAYAK) y su desglose; los contactos del transportista
// se revelan hasta que el viaje se confirma.
// ===========================================================================

const ROUTES = {
  panel: `${CM_BASE}/embarcador`,
  publicar: `${CM_BASE}/embarcador/publicar`,
  cargas: `${CM_BASE}/embarcador/cargas`,
  viajes: `${CM_BASE}/embarcador/viajes`,
  transportistas: `${CM_BASE}/embarcador/transportistas`,
  verificacion: `${CM_BASE}/embarcador/verificacion`,
};

const DEFAULT_COMMISSION = 0.06;
const HOUR_MS = 3600 * 1000;

const LOAD_STATUS: Record<Load["status"], { label: string; tone: Tone; icon: string }> = {
  open: { label: "Abierta", tone: "info", icon: "ri-megaphone-line" },
  awarded: { label: "Asignada", tone: "success", icon: "ri-checkbox-circle-line" },
  cancelled: { label: "Cancelada", tone: "error", icon: "ri-close-circle-line" },
  expired: { label: "Vencida", tone: "neutral", icon: "ri-time-line" },
};

const OFFER_STATUS: Record<Offer["status"], { label: string; tone: Tone }> = {
  active: { label: "Activa", tone: "info" },
  accepted: { label: "Aceptada", tone: "success" },
  rejected: { label: "No elegida", tone: "neutral" },
  withdrawn: { label: "Retirada", tone: "neutral" },
};

const PRICING_ICONS: Record<PricingMode, string> = {
  fixed: "ri-price-tag-3-line",
  offers: "ri-auction-line",
  suggested: "ri-magic-line",
};

const PRICING_HINTS: Record<PricingMode, string> = {
  fixed: "Pones el precio total. El primer transportista verificado que la acepte se queda con la carga.",
  offers: "Los transportistas cotizan durante una ventana de tiempo y tú eliges la mejor oferta.",
  suggested: "Publicamos con la tarifa sugerida por WAYAK para la ruta; el primero que la acepte se la queda.",
};

const DOC_STATUS_META: Record<DocStatus | "missing" | "expired", { label: string; tone: Tone; icon: string }> = {
  approved: { label: "Aprobado", tone: "success", icon: "ri-checkbox-circle-line" },
  pending: { label: "En revisión", tone: "warning", icon: "ri-time-line" },
  rejected: { label: "Rechazado", tone: "error", icon: "ri-close-circle-line" },
  missing: { label: "Falta", tone: "neutral", icon: "ri-upload-2-line" },
  expired: { label: "Vencido", tone: "error", icon: "ri-calendar-close-line" },
};

/** Tile de icono por tono (clases estáticas para que Tailwind las genere). */
const TONE_TILE: Record<Tone, string> = {
  neutral: "bg-subtle text-content-muted",
  primary: "bg-primary-500/10 text-primary-500",
  success: "bg-success/10 text-success",
  warning: "bg-warning/10 text-warning",
  error: "bg-error/10 text-error",
  info: "bg-info/10 text-info",
};

// --- Utilidades --------------------------------------------------------------

const trustRank = (l: TrustLevel) => TRUST_ORDER.indexOf(l);
const commissionOf = (me: Me | null) => me?.catalogs?.commissionPct ?? DEFAULT_COMMISSION;
const ms = (v: string | null | undefined) => toDate(v ?? null)?.getTime() ?? 0;
const monthKey = (d: Date) => toLocalInput(d).slice(0, 7);
const ratingText = (rating: number, count?: number) =>
  rating > 0 ? `★ ${rating.toFixed(1)}${typeof count === "number" ? ` (${count})` : ""}` : "Sin calificaciones";

/** Desglose a partir del TOTAL que paga el embarcador (misma regla que el backend). */
function breakdownFromTotal(total: number, commissionPct: number) {
  const freight = Math.round((total / (1 + commissionPct)) * 100) / 100;
  const commission = Math.round(freight * commissionPct * 100) / 100;
  return { total, freight, commission };
}

function canShip(me: Me | null) {
  return !!me?.profile?.roles?.shipper && !!me?.shipper?.canOperate;
}

/** «Mañana a las 08:00» en hora de México, para el datetime-local. */
function tomorrowAt(hour: number) {
  const day = toLocalInput(new Date(Date.now() + 24 * HOUR_MS)).slice(0, 10);
  return `${day}T${String(hour).padStart(2, "0")}:00`;
}

// --- Piezas internas ----------------------------------------------------------

function SandboxHint() {
  const { me } = useMe();
  if (!me?.profile?.sandboxOf) return null;
  return (
    <div className="mb-4 rounded-xl border border-warning/30 bg-warning/10 px-4 py-3 flex items-center gap-3">
      <Badge tone="warning">Sandbox</Badge>
      <p className="text-sm text-content">Estás viendo datos de ejemplo del modo Sandbox.</p>
    </div>
  );
}

function FormBlock({ title, subtitle, icon, children }: { title: string; subtitle?: string; icon: string; children: ReactNode }) {
  return (
    <Card padding="lg">
      <div className="border-b border-line pb-2 mb-4">
        <h2 className="text-sm font-semibold text-content">
          <i className={cn(icon, "mr-1.5 text-primary-500")} />
          {title}
        </h2>
        {subtitle && <p className="text-xs text-content-muted mt-0.5">{subtitle}</p>}
      </div>
      {children}
    </Card>
  );
}

function Check({ checked, onChange, label, hint, disabled }: { checked: boolean; onChange: (v: boolean) => void; label: ReactNode; hint?: ReactNode; disabled?: boolean }) {
  return (
    <label className={cn("flex items-start gap-2 text-sm text-content", disabled ? "opacity-60" : "cursor-pointer")}>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} className="accent-primary-500 mt-0.5" />
      <span className="min-w-0">
        {label}
        {hint && <span className="block text-xs text-content-muted">{hint}</span>}
      </span>
    </label>
  );
}

function KV({ k, v, strong }: { k: ReactNode; v: ReactNode; strong?: boolean }) {
  return (
    <div className="flex justify-between gap-3 text-sm">
      <dt className="text-content-muted shrink-0">{k}</dt>
      <dd className={cn("text-right text-content min-w-0 truncate", strong && "font-semibold")}>{v}</dd>
    </div>
  );
}

function BlockTitle({ children }: { children: ReactNode }) {
  return <p className="text-xs font-semibold text-content-muted uppercase tracking-wide mb-2">{children}</p>;
}

/** Total + comisión + flete al transportista. */
function PriceBreakdown({ total, commissionPct, label = "Total que pagas" }: { total: number; commissionPct: number; label?: string }) {
  const b = breakdownFromTotal(total, commissionPct);
  return (
    <dl className="space-y-1.5">
      <KV k={label} v={money(b.total)} strong />
      <KV k={`Comisión WAYAK (${pct(commissionPct)})`} v={money(b.commission)} />
      <KV k="Flete que recibe el transportista" v={money(b.freight)} />
    </dl>
  );
}

function LoadStatusBadge({ status }: { status: Load["status"] }) {
  const m = LOAD_STATUS[status] ?? LOAD_STATUS.open;
  return (
    <Badge tone={m.tone}>
      <i className={cn(m.icon, "mr-1")} />
      {m.label}
    </Badge>
  );
}

// ===========================================================================
// 1. Panel
// ===========================================================================

interface TodoItem {
  key: string;
  icon: string;
  tone: Tone;
  title: string;
  detail: string;
  action: string;
  to: string;
}

export function EmbarcadorPanel() {
  const navigate = useNavigate();
  const { me, loading: meLoading, error: meError, reload: reloadMe } = useMe();
  const loads = useAsync(() => exchangeApi.loads("mine"), []);
  const trips = useAsync(() => exchangeApi.trips("shipper"), []);

  const view = useMemo(() => {
    const L = loads.data ?? [];
    const T = trips.data ?? [];
    const open = L.filter((l) => l.status === "open");
    const offersPending = open.reduce((s, l) => s + (l.offersCount || 0), 0);
    const inProgress = T.filter((t) => !["delivered", "closed", "cancelled"].includes(t.status));
    const thisMonth = monthKey(new Date());
    const monthTrips = T.filter((t) => t.status !== "cancelled" && toDate(t.pickupWindow?.from) && monthKey(toDate(t.pickupWindow.from) as Date) === thisMonth);
    const spend = monthTrips.reduce((s, t) => s + (t.pricing?.total || 0), 0);

    const todo: TodoItem[] = [];
    for (const l of open.filter((x) => x.offersCount > 0)) {
      todo.push({
        key: `offers-${l.id}`,
        icon: "ri-auction-line",
        tone: "warning",
        title: `Revisa ${l.offersCount} oferta${l.offersCount === 1 ? "" : "s"} · ${l.folio}`,
        detail: `${l.origin.city} → ${l.destination.city} · mejor oferta ${money(l.bestOfferTotal)}${l.offersCloseAt ? ` · cierra ${relative(l.offersCloseAt)}` : ""}`,
        action: "Ver ofertas",
        to: `${ROUTES.cargas}?id=${l.id}`,
      });
    }
    for (const t of T.filter((x) => x.status === "delivered")) {
      todo.push({
        key: `close-${t.id}`,
        icon: "ri-hand-heart-line",
        tone: "success",
        title: `Confirma la entrega y califica · ${t.folio}`,
        detail: `${t.carrierName} entregó en ${t.destination.city}. Al cerrar se libera el pago.`,
        action: "Cerrar viaje",
        to: `${ROUTES.viajes}?id=${t.id}`,
      });
    }
    for (const t of T.filter((x) => x.status === "awarded")) {
      todo.push({
        key: `awarded-${t.id}`,
        icon: "ri-user-add-line",
        tone: "info",
        title: `Esperando unidad y operador · ${t.folio}`,
        detail: `${t.carrierName} está asignando unidad para ${t.origin.city} → ${t.destination.city}.`,
        action: "Ver viaje",
        to: `${ROUTES.viajes}?id=${t.id}`,
      });
    }
    const now = Date.now();
    for (const l of open.filter((x) => x.offersCount === 0 && x.pricingMode === "offers" && x.offersCloseAt && ms(x.offersCloseAt) < now)) {
      todo.push({
        key: `nooffers-${l.id}`,
        icon: "ri-error-warning-line",
        tone: "error",
        title: `Sin ofertas · ${l.folio}`,
        detail: "La ventana de ofertas cerró sin cotizaciones: invita transportistas compatibles o vuelve a publicar.",
        action: "Ver carga",
        to: `${ROUTES.cargas}?id=${l.id}`,
      });
    }

    const nextPickups = T.filter((t) => ["awarded", "confirmed", "to_pickup", "at_pickup"].includes(t.status))
      .sort((a, b) => ms(a.pickupWindow.from) - ms(b.pickupWindow.from))
      .slice(0, 5);

    return { open, offersPending, inProgress, spend, monthTrips, todo, nextPickups };
  }, [loads.data, trips.data]);

  if (meLoading) return <Section title="Panel del embarcador"><Loader /></Section>;
  if (meError || !me) return <Section title="Panel del embarcador"><ErrorState error={meError || "Sin información"} onRetry={() => void reloadMe()} /></Section>;

  const listsLoading = loads.loading || trips.loading;
  const listsError = loads.error || trips.error;
  const operable = canShip(me);

  return (
    <Section
      title="Panel del embarcador"
      subtitle="Lo que necesita tu atención hoy, el estado de tus cargas y tus viajes."
      actions={
        <Button size="sm" icon={<i className="ri-add-line" />} disabled={!operable} onClick={() => navigate(ROUTES.publicar)}>
          Publicar carga
        </Button>
      }
    >
      <SandboxHint />
      <GateNotice role="shipper" />

      {listsError ? (
        <ErrorState error={listsError} onRetry={() => { void loads.reload(); void trips.reload(); }} />
      ) : listsLoading && !loads.data ? (
        <Loader />
      ) : (
        <div className="space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Kpi label="Cargas abiertas" value={view.open.length} icon="ri-megaphone-line" tone="info" hint="Publicadas y sin asignar" />
            <Kpi label="Ofertas por revisar" value={view.offersPending} icon="ri-auction-line" tone={view.offersPending ? "warning" : "primary"} hint="En cargas abiertas" />
            <Kpi label="Viajes en curso" value={view.inProgress.length} icon="ri-truck-line" tone="primary" hint="Asignados, en ruta o por descargar" />
            <Kpi label="Gasto del mes" value={money(view.spend)} icon="ri-money-dollar-circle-line" tone="success" hint={`${view.monthTrips.length} viaje(s) · total con comisión`} />
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            <Card padding="md" className="lg:col-span-2">
              <div className="flex items-center justify-between gap-2 mb-3">
                <h2 className="text-sm font-semibold text-content"><i className="ri-flashlight-line mr-1.5 text-primary-500" />Te toca</h2>
                {view.todo.length > 0 && <Badge tone="warning">{view.todo.length}</Badge>}
              </div>
              {view.todo.length === 0 ? (
                <EmptyState
                  icon="ri-checkbox-circle-line"
                  title="Nada pendiente"
                  description="No tienes ofertas por revisar ni entregas por confirmar."
                  action={operable ? <Button size="sm" variant="outline" icon={<i className="ri-add-line" />} onClick={() => navigate(ROUTES.publicar)}>Publicar carga</Button> : undefined}
                />
              ) : (
                <ul className="divide-y divide-line">
                  {view.todo.map((t) => (
                    <li key={t.key} className="py-3 flex flex-col sm:flex-row sm:items-center gap-3">
                      <span className={cn("w-9 h-9 rounded-xl flex items-center justify-center shrink-0", TONE_TILE[t.tone])}>
                        <i className={t.icon} />
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-content truncate">{t.title}</p>
                        <p className="text-xs text-content-muted">{t.detail}</p>
                      </div>
                      <Button size="sm" variant="outline" className="shrink-0" onClick={() => navigate(t.to)}>{t.action}</Button>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            <div className="space-y-4">
              <Card padding="md">
                <h2 className="text-sm font-semibold text-content mb-3"><i className="ri-shield-user-line mr-1.5 text-primary-500" />Tu empresa</h2>
                <div className="flex flex-wrap items-center gap-2">
                  <TrustBadge level={me.shipper.level} />
                  {operable ? <Badge tone="success">Puede publicar</Badge> : <Badge tone="warning">Verificación pendiente</Badge>}
                </div>
                <p className="text-xs text-content-muted mt-2">{TRUST_META[me.shipper.level]?.hint}</p>
                <div className="mt-3 grid grid-cols-1 gap-2">
                  <Button size="sm" variant="soft" icon={<i className="ri-search-line" />} onClick={() => navigate(ROUTES.transportistas)}>Buscar transportistas</Button>
                  <Button size="sm" variant="soft" icon={<i className="ri-file-shield-2-line" />} onClick={() => navigate(ROUTES.verificacion)}>Expediente y datos</Button>
                </div>
              </Card>

              <Card padding="md">
                <h2 className="text-sm font-semibold text-content mb-3"><i className="ri-calendar-event-line mr-1.5 text-primary-500" />Próximas cargas</h2>
                {view.nextPickups.length === 0 ? (
                  <p className="text-sm text-content-muted">No hay recolecciones programadas.</p>
                ) : (
                  <ul className="space-y-3">
                    {view.nextPickups.map((t) => (
                      <li key={t.id}>
                        <button type="button" className="w-full text-left" onClick={() => navigate(`${ROUTES.viajes}?id=${t.id}`)}>
                          <RouteLine origin={t.origin} destination={t.destination} className="text-sm" />
                          <div className="flex items-center justify-between gap-2 mt-1">
                            <span className="text-xs text-content-muted truncate">{fmtWindow(t.pickupWindow)} · {t.carrierName}</span>
                            <TripStatusBadge status={t.status} />
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
            </div>
          </div>
        </div>
      )}
    </Section>
  );
}

// ===========================================================================
// 2. Publicar carga
// ===========================================================================

interface LoadForm {
  originKey: string;
  originAddress: string;
  originGeofenceId: string;
  destinationKey: string;
  destinationAddress: string;
  destinationGeofenceId: string;
  pickupFrom: string;
  pickupTo: string;
  deliveryFrom: string;
  deliveryTo: string;
  merchandise: string;
  satKey: string;
  weightTons: string;
  volumeM3: string;
  declaredValue: string;
  hazmat: boolean;
  maneuvers: string;
  unitType: UnitType;
  custody: boolean;
  gps: boolean;
  oea: boolean;
  trustRequired: TrustLevel;
  pricingMode: PricingMode;
  price: string;
  offersCloseAt: string;
  notes: string;
}

const EMPTY_LOAD: LoadForm = {
  originKey: "",
  originAddress: "",
  originGeofenceId: "",
  destinationKey: "",
  destinationAddress: "",
  destinationGeofenceId: "",
  pickupFrom: "",
  pickupTo: "",
  deliveryFrom: "",
  deliveryTo: "",
  merchandise: "",
  satKey: "",
  weightTons: "",
  volumeM3: "",
  declaredValue: "",
  hazmat: false,
  maneuvers: "",
  unitType: "caja_53",
  custody: false,
  gps: true,
  oea: false,
  trustRequired: "verificado",
  pricingMode: "offers",
  price: "",
  offersCloseAt: "",
  notes: "",
};

type LoadErrors = Partial<Record<keyof LoadForm, string>>;

function validateLoad(f: LoadForm, me: Me | null): LoadErrors {
  const e: LoadErrors = {};
  const now = Date.now();
  if (!f.originKey) e.originKey = "Elige la ciudad de origen";
  if (!f.destinationKey) e.destinationKey = "Elige la ciudad de destino";
  if (f.originKey && f.originKey === f.destinationKey) e.destinationKey = "El destino debe ser distinto al origen";
  const pFrom = f.pickupFrom ? Date.parse(fromLocalInput(f.pickupFrom)) : 0;
  if (!pFrom) e.pickupFrom = "Indica desde cuándo se puede cargar";
  else if (pFrom < now - HOUR_MS) e.pickupFrom = "La cita de carga no puede estar en el pasado";
  const pTo = f.pickupTo ? Date.parse(fromLocalInput(f.pickupTo)) : 0;
  if (pTo && pFrom && pTo < pFrom) e.pickupTo = "El fin de la ventana debe ser después del inicio";
  const dFrom = f.deliveryFrom ? Date.parse(fromLocalInput(f.deliveryFrom)) : 0;
  if (dFrom && pFrom && dFrom < pFrom) e.deliveryFrom = "La entrega no puede ser antes de la carga";
  const dTo = f.deliveryTo ? Date.parse(fromLocalInput(f.deliveryTo)) : 0;
  if (dTo && !dFrom) e.deliveryFrom = "Indica el inicio de la ventana de entrega";
  if (dTo && dFrom && dTo < dFrom) e.deliveryTo = "El fin de la ventana debe ser después del inicio";
  if (!f.merchandise.trim()) e.merchandise = "Describe la mercancía";
  const w = Number(f.weightTons);
  const cap = me?.catalogs?.unitTypes?.[f.unitType]?.tons;
  if (!(w > 0)) e.weightTons = "Indica el peso en toneladas";
  else if (cap && w > cap) e.weightTons = `Excede la capacidad de ${UNIT_LABELS[f.unitType]} (${cap} t)`;
  if (f.volumeM3 && !(Number(f.volumeM3) > 0)) e.volumeM3 = "Volumen inválido";
  if (f.declaredValue && !(Number(f.declaredValue) > 0)) e.declaredValue = "Valor inválido";
  if (f.pricingMode === "fixed" && !(Number(f.price) > 0)) e.price = "Indica el precio total que vas a pagar";
  if (f.pricingMode === "offers" && f.offersCloseAt) {
    const close = Date.parse(fromLocalInput(f.offersCloseAt));
    if (close < now) e.offersCloseAt = "El cierre de ofertas debe ser en el futuro";
    else if (pFrom && close > pFrom) e.offersCloseAt = "Debe cerrar antes de la cita de carga";
  }
  return e;
}

function RateBox({ rate, loading, error }: { rate: SuggestedRate | null; loading: boolean; error: string | null }) {
  if (loading) {
    return (
      <div className="rounded-xl border border-line bg-app p-3 text-sm text-content-muted flex items-center gap-2">
        <i className="ri-loader-4-line animate-spin text-primary-500" /> Calculando tarifa sugerida…
      </div>
    );
  }
  if (error) return <div className="rounded-xl border border-warning/30 bg-warning/10 p-3 text-sm text-content">{error}</div>;
  if (!rate) {
    return <div className="rounded-xl border border-line bg-app p-3 text-sm text-content-muted">Elige origen, destino y tipo de unidad para ver la tarifa sugerida.</div>;
  }
  return (
    <div className="rounded-xl border border-info/30 bg-info/10 p-3 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-semibold text-content">
          Tarifa sugerida: {money(rate.totalMin)} – {money(rate.totalMax)}
        </p>
        <Badge tone={rate.samples === "history" ? "success" : "neutral"}>
          {rate.samples === "history" ? "Basado en historial de la ruta" : "Basado en modelo de costos"}
        </Badge>
      </div>
      <p className="text-xs text-content-muted mt-1">
        {rate.km} km aprox. · referencia {money(rate.totalSuggested)} · total con comisión WAYAK ({pct(rate.commissionPct)}) incluida
      </p>
    </div>
  );
}

export function PublicarCargaSection() {
  const navigate = useNavigate();
  const { me, loading: meLoading, error: meError, reload: reloadMe } = useMe();
  const [f, setF] = useState<LoadForm>(() => ({ ...EMPTY_LOAD, pickupFrom: tomorrowAt(8), pickupTo: tomorrowAt(12) }));
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rate, setRate] = useState<SuggestedRate | null>(null);
  const [rateLoading, setRateLoading] = useState(false);
  const [rateError, setRateError] = useState<string | null>(null);

  const set = <K extends keyof LoadForm>(k: K, v: LoadForm[K]) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (!f.originKey || !f.destinationKey || f.originKey === f.destinationKey) {
      setRate(null);
      setRateError(null);
      return;
    }
    let alive = true;
    setRateLoading(true);
    setRateError(null);
    exchangeApi
      .suggestedRate({ origin: f.originKey, destination: f.destinationKey, unitType: f.unitType })
      .then((r) => alive && setRate(r))
      .catch((err) => {
        if (!alive) return;
        setRate(null);
        setRateError(apiErrorMessage(err, "No se pudo calcular la tarifa sugerida"));
      })
      .finally(() => alive && setRateLoading(false));
    return () => {
      alive = false;
    };
  }, [f.originKey, f.destinationKey, f.unitType]);

  const errors = useMemo(() => validateLoad(f, me), [f, me]);
  const shown = (k: keyof LoadForm) => (touched ? errors[k] : undefined);

  if (meLoading) return <Section title="Publicar carga"><Loader /></Section>;
  if (meError || !me) return <Section title="Publicar carga"><ErrorState error={meError || "Sin información"} onRetry={() => void reloadMe()} /></Section>;

  const operable = canShip(me);
  const commissionPct = commissionOf(me);
  const unitSpecs = me.catalogs.unitTypes;
  const geofences = me.fleet?.geofences ?? [];
  const forcedTracked = f.hazmat || f.gps;
  const effectiveTrust: TrustLevel = forcedTracked && trustRank(f.trustRequired) < trustRank("rastreado") ? "rastreado" : f.trustRequired;
  const capacity = unitSpecs[f.unitType]?.tons;

  const totalForBreakdown =
    f.pricingMode === "fixed" ? Number(f.price) || 0 : f.pricingMode === "suggested" ? rate?.totalSuggested || 0 : 0;

  const geofenceOptions = [{ value: "", label: geofences.length ? "Ninguna" : "No tienes geocercas en tu flotilla" }, ...geofences.map((g) => ({ value: g.id, label: `${g.name}${g.address ? ` · ${g.address}` : ""}` }))];
  const pickGeofence = (side: "origin" | "destination", id: string) => {
    const g = geofences.find((x) => x.id === id);
    setF((p) =>
      side === "origin"
        ? { ...p, originGeofenceId: id, originAddress: p.originAddress || g?.address || "" }
        : { ...p, destinationGeofenceId: id, destinationAddress: p.destinationAddress || g?.address || "" },
    );
  };

  const submit = async () => {
    setTouched(true);
    if (Object.keys(errors).length) {
      toast.error("Revisa los campos marcados");
      return;
    }
    const place = (key: string, address: string, geofenceId: string) => ({
      key,
      ...(address.trim() ? { address: address.trim() } : {}),
      ...(geofenceId ? { geofenceId } : {}),
    });
    const body: Record<string, unknown> = {
      origin: place(f.originKey, f.originAddress, f.originGeofenceId),
      destination: place(f.destinationKey, f.destinationAddress, f.destinationGeofenceId),
      pickupWindow: { from: fromLocalInput(f.pickupFrom), to: fromLocalInput(f.pickupTo || f.pickupFrom) },
      deliveryWindow: f.deliveryFrom ? { from: fromLocalInput(f.deliveryFrom), to: fromLocalInput(f.deliveryTo || f.deliveryFrom) } : null,
      merchandise: f.merchandise.trim(),
      satKey: f.satKey.trim() || null,
      weightTons: Number(f.weightTons),
      volumeM3: f.volumeM3 ? Number(f.volumeM3) : null,
      declaredValue: f.declaredValue ? Number(f.declaredValue) : null,
      hazmat: f.hazmat,
      maneuvers: f.maneuvers.trim() || null,
      unitType: f.unitType,
      requirements: { custody: f.custody, gps: f.gps, oea: f.oea },
      trustRequired: effectiveTrust,
      pricingMode: f.pricingMode,
      price: f.pricingMode === "fixed" ? Number(f.price) : null,
      offersCloseAt: f.pricingMode === "offers" && f.offersCloseAt ? fromLocalInput(f.offersCloseAt) : null,
      notes: f.notes.trim() || null,
    };
    setSaving(true);
    try {
      const load = await exchangeApi.createLoad(body);
      toast.success(`Carga ${load.folio} publicada`);
      navigate(`${ROUTES.cargas}?id=${load.id}`);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Section title="Publicar carga" subtitle="Describe tu embarque; los transportistas verificados compatibles reciben aviso al instante.">
      <SandboxHint />
      <GateNotice role="shipper" />
      <fieldset disabled={!operable || saving} className="max-w-4xl mx-auto space-y-4 min-w-0">
        <FormBlock title="Ruta" icon="ri-route-line" subtitle="Ciudad de origen y destino; la dirección exacta se comparte al confirmar el viaje.">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-3">
              <CitySelect label="Origen" required value={f.originKey} onChange={(v) => set("originKey", v)} error={shown("originKey")} />
              <Select label="Dirección frecuente (geocerca)" value={f.originGeofenceId} onChange={(e) => pickGeofence("origin", e.target.value)} options={geofenceOptions} disabled={!geofences.length} />
              <Input label="Dirección de carga (opcional)" value={f.originAddress} onChange={(e) => set("originAddress", e.target.value)} placeholder="Calle, número, parque industrial" />
            </div>
            <div className="space-y-3">
              <CitySelect label="Destino" required value={f.destinationKey} onChange={(v) => set("destinationKey", v)} error={shown("destinationKey")} />
              <Select label="Dirección frecuente (geocerca)" value={f.destinationGeofenceId} onChange={(e) => pickGeofence("destination", e.target.value)} options={geofenceOptions} disabled={!geofences.length} />
              <Input label="Dirección de entrega (opcional)" value={f.destinationAddress} onChange={(e) => set("destinationAddress", e.target.value)} placeholder="Calle, número, CEDIS" />
            </div>
          </div>
        </FormBlock>

        <FormBlock title="Ventanas de cita" icon="ri-calendar-schedule-line" subtitle="Hora del centro de México.">
          <div className="grid gap-4 sm:grid-cols-2">
            <Input type="datetime-local" label={<>Carga desde<span className="text-error"> *</span></>} value={f.pickupFrom} onChange={(e) => set("pickupFrom", e.target.value)} error={shown("pickupFrom")} />
            <Input type="datetime-local" label="Carga hasta" value={f.pickupTo} onChange={(e) => set("pickupTo", e.target.value)} error={shown("pickupTo")} />
            <Input type="datetime-local" label="Entrega desde" value={f.deliveryFrom} onChange={(e) => set("deliveryFrom", e.target.value)} error={shown("deliveryFrom")} />
            <Input type="datetime-local" label="Entrega hasta" value={f.deliveryTo} onChange={(e) => set("deliveryTo", e.target.value)} error={shown("deliveryTo")} />
          </div>
        </FormBlock>

        <FormBlock title="Mercancía" icon="ri-box-3-line">
          <div className="grid gap-4 sm:grid-cols-2">
            <Input label={<>Mercancía<span className="text-error"> *</span></>} value={f.merchandise} onChange={(e) => set("merchandise", e.target.value)} placeholder="Ej. Refacciones automotrices en tarimas" error={shown("merchandise")} containerClassName="sm:col-span-2" />
            <Input label="Clave SAT (Carta Porte)" value={f.satKey} onChange={(e) => set("satKey", e.target.value)} placeholder="Ej. 25172000" hint="Clave de producto del catálogo c_ClaveProdServCP" />
            <Input type="number" min={0} step="0.1" label={<>Peso (toneladas)<span className="text-error"> *</span></>} value={f.weightTons} onChange={(e) => set("weightTons", e.target.value)} error={shown("weightTons")} hint={capacity ? `Máximo ${capacity} t en ${UNIT_LABELS[f.unitType]}` : undefined} />
            <Input type="number" min={0} step="0.1" label="Volumen (m³)" value={f.volumeM3} onChange={(e) => set("volumeM3", e.target.value)} error={shown("volumeM3")} />
            <Input type="number" min={0} step="1000" label="Valor declarado (MXN)" value={f.declaredValue} onChange={(e) => set("declaredValue", e.target.value)} error={shown("declaredValue")} hint="Sirve para el seguro de la carga" />
            <Input label="Maniobras" value={f.maneuvers} onChange={(e) => set("maneuvers", e.target.value)} placeholder="Ej. Carga con montacargas, descarga a mano" containerClassName="sm:col-span-2" />
            <div className="sm:col-span-2">
              <Check checked={f.hazmat} onChange={(v) => set("hazmat", v)} label="Material peligroso" hint="Requiere transportista Rastreado o superior y unidad autorizada." />
            </div>
          </div>
        </FormBlock>

        <FormBlock title="Unidad y requisitos" icon="ri-truck-line">
          <div className="grid gap-4 sm:grid-cols-2">
            <Select
              label={<>Tipo de unidad<span className="text-error"> *</span></>}
              value={f.unitType}
              onChange={(e) => set("unitType", e.target.value as UnitType)}
              options={(Object.keys(UNIT_LABELS) as UnitType[]).map((u) => ({ value: u, label: `${UNIT_LABELS[u]} · hasta ${unitSpecs[u]?.tons ?? "—"} t` }))}
            />
            <div>
              <Select
                label="Nivel mínimo del transportista"
                value={effectiveTrust}
                onChange={(e) => set("trustRequired", e.target.value as TrustLevel)}
                options={(["verificado", "rastreado", "premium"] as TrustLevel[]).map((l) => ({
                  value: l,
                  label: `${TRUST_META[l].label} — ${TRUST_META[l].hint}`,
                  disabled: forcedTracked && trustRank(l) < trustRank("rastreado"),
                }))}
                hint={forcedTracked ? "Pedir GPS o mover material peligroso obliga a Rastreado o superior." : "Solo transportistas con este nivel o mayor podrán tomarla."}
              />
            </div>
            <div className="sm:col-span-2 grid gap-3 sm:grid-cols-3">
              <Check checked={f.gps} onChange={(v) => set("gps", v)} label="GPS integrado a WAYAK" hint="Ves la unidad en vivo durante el viaje." />
              <Check checked={f.custody} onChange={(v) => set("custody", v)} label="Custodia" hint="Escolta armada o monitoreo dedicado." />
              <Check checked={f.oea} onChange={(v) => set("oea", v)} label="Certificación OEA / CTPAT" hint="Para cargas de comercio exterior." />
            </div>
          </div>
        </FormBlock>

        <FormBlock title="Cómo contratar" icon="ri-hand-coin-line" subtitle="Todos los precios son TOTALES: incluyen el flete y la comisión de WAYAK.">
          <div className="grid gap-3 sm:grid-cols-3">
            {(["fixed", "offers", "suggested"] as PricingMode[]).map((m) => {
              const active = f.pricingMode === m;
              return (
                <button
                  key={m}
                  type="button"
                  onClick={() => set("pricingMode", m)}
                  aria-pressed={active}
                  className={cn("text-left rounded-xl border p-4 transition-colors", active ? "border-primary-500 bg-primary-500/10" : "border-line bg-app hover:border-primary-500/60")}
                >
                  <div className="flex items-center justify-between gap-2">
                    <i className={cn(PRICING_ICONS[m], "text-xl", active ? "text-primary-500" : "text-content-muted")} />
                    {active && <i className="ri-checkbox-circle-fill text-primary-500" />}
                  </div>
                  <p className="text-sm font-semibold text-content mt-2">{PRICING_LABELS[m]}</p>
                  <p className="text-xs text-content-muted mt-1">{PRICING_HINTS[m]}</p>
                </button>
              );
            })}
          </div>

          <div className="mt-4 space-y-4">
            <RateBox rate={rate} loading={rateLoading} error={rateError} />

            {f.pricingMode === "fixed" && (
              <div className="grid gap-3 sm:grid-cols-2 sm:items-end">
                <Input
                  type="number"
                  min={0}
                  step="100"
                  label={<>Precio total (MXN)<span className="text-error"> *</span></>}
                  value={f.price}
                  onChange={(e) => set("price", e.target.value)}
                  error={shown("price")}
                  hint="Lo que pagas en total, comisión incluida."
                />
                {rate && (
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => set("price", String(Math.round(rate.totalSuggested)))}>Usar sugerida ({money(rate.totalSuggested)})</Button>
                  </div>
                )}
              </div>
            )}

            {f.pricingMode === "offers" && (
              <Input
                type="datetime-local"
                label="Cierre de ofertas"
                value={f.offersCloseAt}
                onChange={(e) => set("offersCloseAt", e.target.value)}
                error={shown("offersCloseAt")}
                hint="Si lo dejas vacío, cierra en 24 h o al inicio de la cita de carga (lo que pase primero)."
              />
            )}

            {totalForBreakdown > 0 ? (
              <div className="rounded-xl border border-line bg-app p-4">
                <BlockTitle>Desglose</BlockTitle>
                <PriceBreakdown total={totalForBreakdown} commissionPct={commissionPct} />
                {f.pricingMode === "fixed" && rate && (Number(f.price) < rate.totalMin || Number(f.price) > rate.totalMax) && (
                  <p className="text-xs text-warning mt-2">
                    <i className="ri-error-warning-line mr-1" />
                    Tu precio está {Number(f.price) < rate.totalMin ? "por debajo" : "por encima"} del rango sugerido; {Number(f.price) < rate.totalMin ? "puede tardar en tomarse." : "revisa que sea correcto."}
                  </p>
                )}
              </div>
            ) : f.pricingMode === "offers" ? (
              <p className="text-xs text-content-muted">
                Verás cada oferta como total (flete + comisión WAYAK de {pct(commissionPct)}) con su desglose, el nivel y la calificación del transportista.
              </p>
            ) : null}

            <Textarea label="Instrucciones adicionales (opcional)" rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Horario de andén, equipo de seguridad, contacto en sitio (se comparte al confirmar)…" />
          </div>
        </FormBlock>

        <div className="flex gap-3 border-t border-line pt-4">
          <Button variant="outline" size="md" shape="pill" className="flex-1" onClick={() => navigate(ROUTES.panel)}>Cancelar</Button>
          <Button size="md" shape="pill" className="flex-1" loading={saving} disabled={!operable} onClick={submit}>Publicar carga</Button>
        </div>
      </fieldset>
    </Section>
  );
}

// ===========================================================================
// 3. Mis cargas
// ===========================================================================

type LoadTab = "open" | "awarded" | "cancelled";
const tabOfLoad = (l: Load): LoadTab => (l.status === "open" ? "open" : l.status === "awarded" ? "awarded" : "cancelled");

function LoadCard({ load, onOpen }: { load: Load; onOpen: () => void }) {
  return (
    <EntityCard onClick={onOpen} accent={load.status === "open" && load.offersCount > 0 ? "warning" : "neutral"}>
      <EntityCardHeader
        icon={<i className={UNIT_ICONS[load.unitType]} />}
        iconTone={LOAD_STATUS[load.status]?.tone ?? "neutral"}
        title={<RouteLine origin={load.origin} destination={load.destination} />}
        subtitle={`${load.folio} · ${load.merchandise}`}
        actions={<LoadStatusBadge status={load.status} />}
      />
      <EntityCardBody>
        <EntityCardRow label="Carga">{fmtWindow(load.pickupWindow)}</EntityCardRow>
        <EntityCardRow label="Unidad">{UNIT_LABELS[load.unitType]} · {load.weightTons} t</EntityCardRow>
        <EntityCardRow label="Modalidad">{PRICING_LABELS[load.pricingMode]}</EntityCardRow>
        <EntityCardRow label={load.pricingMode === "offers" ? "Mejor oferta" : "Precio total"}>
          {load.pricingMode === "offers" ? money(load.bestOfferTotal) : money(load.price)}
        </EntityCardRow>
        <EntityCardRow label="Ofertas">
          {load.offersCount > 0 ? <Badge tone={load.status === "open" ? "warning" : "neutral"} size="sm">{load.offersCount}</Badge> : "0"}
        </EntityCardRow>
        {load.status === "open" && load.offersCloseAt && <EntityCardRow label="Cierre de ofertas">{relative(load.offersCloseAt)}</EntityCardRow>}
      </EntityCardBody>
      <EntityCardActions>
        <Button size="sm" variant={load.status === "open" && load.offersCount > 0 ? "primary" : "outline"} className="flex-1" onClick={(e) => { e.stopPropagation(); onOpen(); }}>
          {load.status === "open" && load.offersCount > 0 ? "Revisar ofertas" : "Ver detalle"}
        </Button>
      </EntityCardActions>
      <EntityCardFooter>Publicada {relative(load.createdAt) || "—"}</EntityCardFooter>
    </EntityCard>
  );
}

function LoadDetailModal({ id, onClose, onChanged, onAccepted }: { id: string; onClose: () => void; onChanged: () => void; onAccepted: (t: Trip) => void }) {
  const { me } = useMe();
  const detail = useAsync(() => exchangeApi.load(id), [id]);
  const [busy, setBusy] = useState<string | null>(null);
  const [invited, setInvited] = useState<string[]>([]);
  const commissionPct = commissionOf(me);

  const load = detail.data?.load;
  const offers = useMemo(() => [...(detail.data?.offers ?? [])].sort((a, b) => a.total - b.total), [detail.data]);
  const matches = detail.data?.matches ?? [];
  const isOpen = load?.status === "open";
  const invitedIds = new Set([...(load?.invitedCarrierIds ?? []), ...invited]);

  const accept = async (o: Offer) => {
    const ok = await confirmDialog({
      title: `¿Aceptar la oferta de ${o.carrierName}?`,
      html: `Pagarás <b>${money(o.total)}</b> en total (flete ${money(o.freight)} + comisión WAYAK ${money(o.pricing?.commission)}).<br/>Las demás ofertas se rechazan y se crea el viaje.`,
      confirmText: "Aceptar oferta",
    });
    if (!ok) return;
    setBusy(o.id);
    try {
      const trip = await exchangeApi.acceptOffer(o.id);
      toast.success(`Oferta aceptada · viaje ${trip.folio} creado`);
      onChanged();
      onAccepted(trip);
    } catch (err) {
      toast.error(apiErrorMessage(err));
      void detail.reload();
    } finally {
      setBusy(null);
    }
  };

  const doInvite = async (m: Match) => {
    if (!load) return;
    setBusy(`inv-${m.capacity.id}`);
    try {
      await exchangeApi.invite(load.id, m.carrier.companyId);
      setInvited((p) => [...p, m.carrier.companyId]);
      toast.success(`Invitamos a ${m.carrier.name} a cotizar`);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const cancel = async () => {
    if (!load) return;
    const ok = await confirmDialog({
      title: `¿Cancelar la carga ${load.folio}?`,
      text: load.offersCount ? "Las ofertas activas se rechazarán y avisaremos a los transportistas." : "Dejará de aparecer en la bolsa de carga.",
      confirmText: "Cancelar carga",
      cancelText: "Volver",
      tone: "danger",
      icon: "warning",
    });
    if (!ok) return;
    setBusy("cancel");
    try {
      await exchangeApi.cancelLoad(load.id);
      toast.success(`Carga ${load.folio} cancelada`);
      onChanged();
      void detail.reload();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const offerColumns: TableColumn<Offer>[] = [
    {
      key: "carrier",
      header: "Transportista",
      render: (o) => (
        <div className="min-w-0">
          <p className="font-medium text-content truncate">{o.carrierName}</p>
          <div className="flex flex-wrap items-center gap-1.5 mt-1">
            <TrustBadge level={o.carrierLevel} size="sm" />
            <span className="text-xs text-content-muted">{ratingText(o.carrierRating ?? 0)} · {o.carrierTrips ?? 0} viajes</span>
          </div>
        </div>
      ),
    },
    { key: "total", header: "Total", align: "right", render: (o) => <span className="font-semibold text-content">{money(o.total)}</span> },
    { key: "freight", header: "Flete", align: "right", hideOnMobile: true, render: (o) => <span className="text-content-muted">{money(o.freight)}</span> },
    { key: "note", header: "Nota", hideOnMobile: true, render: (o) => <span className="text-xs text-content-muted">{o.note || "—"}</span> },
    {
      key: "action",
      header: "",
      align: "right",
      render: (o) =>
        isOpen && o.status === "active" ? (
          <Button size="sm" loading={busy === o.id} disabled={!!busy} onClick={() => void accept(o)}>Aceptar</Button>
        ) : (
          <Badge tone={OFFER_STATUS[o.status]?.tone ?? "neutral"}>{OFFER_STATUS[o.status]?.label ?? o.status}</Badge>
        ),
    },
  ];

  let body: ReactNode;
  if (detail.loading && !detail.data) body = <Loader />;
  else if (detail.error || !load) body = <ErrorState error={detail.error || "Carga no encontrada"} onRetry={() => void detail.reload()} />;
  else {
    body = (
      <div className="space-y-5">
        <div className="flex flex-wrap items-center gap-2">
          <LoadStatusBadge status={load.status} />
          <Badge tone="neutral"><i className={cn(PRICING_ICONS[load.pricingMode], "mr-1")} />{PRICING_LABELS[load.pricingMode]}</Badge>
          <span title="Nivel mínimo que pediste"><Badge tone="neutral">Mínimo: {TRUST_META[load.trustRequired]?.label}</Badge></span>
          {load.hazmat && <Badge tone="error"><i className="ri-alert-line mr-1" />Material peligroso</Badge>}
          {load.sandboxOf && <Badge tone="warning">Ejemplo (Sandbox)</Badge>}
        </div>
        <RouteLine origin={load.origin} destination={load.destination} className="text-base" />

        <div className="grid sm:grid-cols-2 gap-3">
          <Card padding="md">
            <BlockTitle>Carga</BlockTitle>
            <dl className="space-y-1.5">
              <KV k="Mercancía" v={`${load.merchandise}${load.satKey ? ` · SAT ${load.satKey}` : ""}`} />
              <KV k="Peso / volumen" v={`${load.weightTons} t${load.volumeM3 ? ` · ${load.volumeM3} m³` : ""}`} />
              <KV k="Unidad" v={UNIT_LABELS[load.unitType]} />
              <KV k="Cita de carga" v={fmtWindow(load.pickupWindow)} />
              <KV k="Cita de entrega" v={fmtWindow(load.deliveryWindow)} />
              <KV k="Requisitos" v={[load.requirements.gps && "GPS", load.requirements.custody && "Custodia", load.requirements.oea && "OEA"].filter(Boolean).join(" · ") || "—"} />
              {load.origin.address && <KV k="Dirección de carga" v={load.origin.address} />}
            </dl>
          </Card>
          <Card padding="md">
            <BlockTitle>Precio</BlockTitle>
            {load.price ? (
              <PriceBreakdown total={load.price} commissionPct={commissionPct} />
            ) : load.bestOfferTotal ? (
              <PriceBreakdown total={load.bestOfferTotal} commissionPct={commissionPct} label="Mejor oferta (total)" />
            ) : (
              <p className="text-sm text-content-muted">Aún sin ofertas.</p>
            )}
            {load.suggested && <p className="text-xs text-content-muted mt-2">Rango sugerido: {money(load.suggested.min)} – {money(load.suggested.max)} · {load.suggested.km} km</p>}
            {load.offersCloseAt && isOpen && <p className="text-xs text-content-muted mt-1">Ofertas cierran {fmtDateTime(load.offersCloseAt)} ({relative(load.offersCloseAt)})</p>}
          </Card>
        </div>

        {load.status === "awarded" && load.tripId && (
          <div className="rounded-xl border border-success/30 bg-success/10 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
            <i className="ri-checkbox-circle-line text-2xl text-success" />
            <p className="flex-1 text-sm text-content">Carga asignada. Sigue el viaje (y los datos de contacto) en <b>Mis viajes</b>.</p>
            <Button
              size="sm"
              variant="successSoft"
              onClick={async () => {
                try {
                  onAccepted(await exchangeApi.trip(load.tripId as string));
                } catch (err) {
                  toast.error(apiErrorMessage(err));
                }
              }}
            >
              Ver viaje
            </Button>
          </div>
        )}

        <div>
          <div className="flex items-center justify-between gap-2 mb-2">
            <BlockTitle>Ofertas ({offers.length})</BlockTitle>
          </div>
          {offers.length === 0 ? (
            <EmptyState icon="ri-auction-line" title="Aún no hay ofertas" description={isOpen ? "Invita a transportistas con capacidad compatible para recibir cotizaciones más rápido." : "Esta carga no recibió ofertas."} />
          ) : (
            <Table columns={offerColumns} rows={offers} rowKey={(o) => o.id} compact />
          )}
          <p className="text-xs text-content-faint mt-2"><i className="ri-lock-line mr-1" />Los datos de contacto del transportista se revelan al confirmar el viaje.</p>
        </div>

        {isOpen && (
          <div>
            <BlockTitle>Capacidad compatible ({matches.length})</BlockTitle>
            {matches.length === 0 ? (
              <p className="text-sm text-content-muted">No hay unidades verificadas publicadas para esta ruta y fecha por ahora. Te avisaremos cuando alguien cotice.</p>
            ) : (
              <ul className="grid gap-2 sm:grid-cols-2">
                {matches.map((m) => {
                  const already = invitedIds.has(m.carrier.companyId);
                  return (
                    <li key={m.capacity.id} className="rounded-xl border border-line bg-app p-3 flex flex-col gap-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-content truncate">{m.carrier.name}</p>
                          <div className="flex flex-wrap items-center gap-1.5 mt-1">
                            <TrustBadge level={m.carrier.level} size="sm" />
                            {m.capacity.isBackhaul && <Badge tone="success" size="sm">Retorno vacío</Badge>}
                            <span className="text-xs text-content-muted">{ratingText(m.carrier.rating)} · {m.carrier.trips} viajes</span>
                          </div>
                        </div>
                        <Badge tone={m.score >= 75 ? "success" : m.score >= 50 ? "info" : "neutral"}>{Math.round(m.score)}%</Badge>
                      </div>
                      <p className="text-xs text-content-muted">
                        {UNIT_LABELS[m.capacity.unitType]}{m.capacity.hasGps ? " · con GPS" : ""} · disponible {fmtDay(m.capacity.availableAt)}
                        {m.capacity.rate ? ` · tarifa ${money(m.capacity.rate)}` : ""}
                      </p>
                      <Button size="sm" variant={already ? "soft" : "outline"} disabled={already || !!busy} loading={busy === `inv-${m.capacity.id}`} onClick={() => void doInvite(m)} icon={<i className={already ? "ri-check-line" : "ri-mail-send-line"} />}>
                        {already ? "Invitado" : "Invitar a cotizar"}
                      </Button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}

        {isOpen && (
          <div className="flex flex-wrap gap-2 border-t border-line pt-4">
            <Button size="sm" variant="dangerSoft" icon={<i className="ri-close-circle-line" />} loading={busy === "cancel"} disabled={!!busy} onClick={() => void cancel()}>
              Cancelar carga
            </Button>
          </div>
        )}
      </div>
    );
  }

  return <Modal title={load ? `Carga ${load.folio}` : "Carga"} onClose={onClose} width="w-full max-w-4xl" content={body} />;
}

export function MisCargasSection() {
  const navigate = useNavigate();
  const { me } = useMe();
  const [params, setParams] = useSearchParams();
  const loads = useAsync(() => exchangeApi.loads("mine"), []);
  const [tab, setTab] = useState<LoadTab>("open");
  const [trip, setTrip] = useState<Trip | null>(null);
  const openId = params.get("id");

  const counts = useMemo(() => {
    const c: Record<LoadTab, number> = { open: 0, awarded: 0, cancelled: 0 };
    for (const l of loads.data ?? []) c[tabOfLoad(l)] += 1;
    return c;
  }, [loads.data]);
  const rows = (loads.data ?? []).filter((l) => tabOfLoad(l) === tab);

  const openLoad = (id: string) => setParams((p) => { const n = new URLSearchParams(p); n.set("id", id); return n; });
  const closeLoad = () => setParams((p) => { const n = new URLSearchParams(p); n.delete("id"); return n; });

  return (
    <Section
      title="Mis cargas"
      subtitle="Tus publicaciones, las ofertas que recibes y los transportistas compatibles."
      actions={<Button size="sm" icon={<i className="ri-add-line" />} disabled={!canShip(me)} onClick={() => navigate(ROUTES.publicar)}>Publicar carga</Button>}
      filters={
        <Tabs
          activeId={tab}
          onChange={(id) => setTab(id as LoadTab)}
          items={[
            { id: "open", label: "Abiertas", icon: "ri-megaphone-line", count: counts.open, countLoading: loads.loading && !loads.data },
            { id: "awarded", label: "Asignadas", icon: "ri-checkbox-circle-line", count: counts.awarded, countLoading: loads.loading && !loads.data },
            { id: "cancelled", label: "Canceladas", icon: "ri-close-circle-line", count: counts.cancelled, countLoading: loads.loading && !loads.data },
          ]}
        />
      }
    >
      <SandboxHint />
      <GateNotice role="shipper" />
      {loads.loading && !loads.data ? (
        <Loader />
      ) : loads.error ? (
        <ErrorState error={loads.error} onRetry={() => void loads.reload()} />
      ) : rows.length === 0 ? (
        <EmptyState
          icon="ri-inbox-line"
          title={tab === "open" ? "No tienes cargas abiertas" : tab === "awarded" ? "Aún no tienes cargas asignadas" : "Sin cargas canceladas"}
          description={tab === "open" ? "Publica una carga y los transportistas verificados compatibles recibirán aviso." : undefined}
          action={tab === "open" && canShip(me) ? <Button size="sm" icon={<i className="ri-add-line" />} onClick={() => navigate(ROUTES.publicar)}>Publicar carga</Button> : undefined}
        />
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {rows.map((l) => <LoadCard key={l.id} load={l} onOpen={() => openLoad(l.id)} />)}
        </div>
      )}

      {openId && !trip && (
        <LoadDetailModal
          key={openId}
          id={openId}
          onClose={closeLoad}
          onChanged={() => void loads.reload()}
          onAccepted={(t) => {
            closeLoad();
            setTrip(t);
          }}
        />
      )}
      {trip && <TripDetailModal trip={trip} onClose={() => setTrip(null)} onChanged={() => void loads.reload()} />}
    </Section>
  );
}

// ===========================================================================
// 4. Mis viajes (como embarcador)
// ===========================================================================

type TripTab = "active" | "toClose" | "closed" | "cancelled";
const tabOfTrip = (t: Trip): TripTab =>
  t.status === "delivered" ? "toClose" : t.status === "closed" ? "closed" : t.status === "cancelled" ? "cancelled" : "active";

export function ViajesEmbarcadorSection() {
  const [params, setParams] = useSearchParams();
  const trips = useAsync(() => exchangeApi.trips("shipper"), []);
  const [tab, setTab] = useState<TripTab>("active");
  const [selected, setSelected] = useState<Trip | null>(null);
  const urlId = params.get("id");

  useEffect(() => {
    if (!urlId || selected?.id === urlId || trips.loading) return;
    const found = trips.data?.find((t) => t.id === urlId);
    if (found) {
      setSelected(found);
      setTab(tabOfTrip(found));
      return;
    }
    let alive = true;
    exchangeApi
      .trip(urlId)
      .then((t) => alive && setSelected(t))
      .catch((err) => toast.error(apiErrorMessage(err, "No se encontró el viaje")));
    return () => {
      alive = false;
    };
  }, [urlId, trips.data, trips.loading, selected?.id]);

  const counts = useMemo(() => {
    const c: Record<TripTab, number> = { active: 0, toClose: 0, closed: 0, cancelled: 0 };
    for (const t of trips.data ?? []) c[tabOfTrip(t)] += 1;
    return c;
  }, [trips.data]);
  const rows = (trips.data ?? []).filter((t) => tabOfTrip(t) === tab).sort((a, b) => ms(a.pickupWindow.from) - ms(b.pickupWindow.from));
  const countLoading = trips.loading && !trips.data;

  const open = (t: Trip) => {
    setSelected(t);
    setParams((p) => { const n = new URLSearchParams(p); n.set("id", t.id); return n; });
  };
  const close = () => {
    setSelected(null);
    setParams((p) => { const n = new URLSearchParams(p); n.delete("id"); return n; });
  };
  const changed = (t: Trip) => {
    trips.setData((prev) => (prev ? prev.map((x) => (x.id === t.id ? t : x)) : prev));
    void trips.reload();
  };

  return (
    <Section
      title="Mis viajes"
      subtitle="Sigue cada viaje en sus 8 pasos, confirma la entrega y califica al transportista."
      filters={
        <Tabs
          activeId={tab}
          onChange={(id) => setTab(id as TripTab)}
          items={[
            { id: "active", label: "Activos", icon: "ri-truck-line", count: counts.active, countLoading },
            { id: "toClose", label: "Por cerrar", icon: "ri-hand-heart-line", count: counts.toClose, countLoading },
            { id: "closed", label: "Cerrados", icon: "ri-flag-2-line", count: counts.closed, countLoading },
            { id: "cancelled", label: "Cancelados", icon: "ri-close-circle-line", count: counts.cancelled, countLoading },
          ]}
        />
      }
    >
      <SandboxHint />
      {tab === "toClose" && counts.toClose > 0 && (
        <div className="mb-4 rounded-xl border border-success/30 bg-success/10 p-3 text-sm text-content">
          <i className="ri-information-line mr-1 text-success" />
          Revisa la evidencia de entrega (POD) y cierra el viaje para liberar el pago al transportista.
        </div>
      )}
      {trips.loading && !trips.data ? (
        <Loader />
      ) : trips.error ? (
        <ErrorState error={trips.error} onRetry={() => void trips.reload()} />
      ) : (
        <TripList trips={rows} onOpen={open} />
      )}
      {selected && <TripDetailModal key={selected.id} trip={selected} onClose={close} onChanged={changed} />}
    </Section>
  );
}

// ===========================================================================
// 5. Directorio de transportistas
// ===========================================================================

export function TransportistasSection() {
  const carriers = useAsync(() => exchangeApi.carriers(), []);
  const [q, setQ] = useState("");
  const [level, setLevel] = useState<"" | TrustLevel>("");
  const [unit, setUnit] = useState<"" | UnitType>("");

  const rows = useMemo(() => {
    const term = q.trim().toLowerCase();
    return (carriers.data ?? []).filter(
      (c) =>
        (!term || c.name.toLowerCase().includes(term) || c.lanes.some((l) => l.toLowerCase().includes(term))) &&
        (!level || c.level === level) &&
        (!unit || c.unitTypes.includes(unit)),
    );
  }, [carriers.data, q, level, unit]);

  return (
    <Section
      title="Transportistas"
      subtitle="Empresas de transporte en Cargo Market, con su nivel de confianza, calificación y capacidad disponible."
      filters={
        <div className="grid gap-3 sm:grid-cols-3">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre o ruta" icon={<i className="ri-search-line" />} aria-label="Buscar transportista" />
          <Select
            value={level}
            aria-label="Nivel de confianza"
            onChange={(e) => setLevel(e.target.value as "" | TrustLevel)}
            options={[{ value: "", label: "Todos los niveles" }, ...TRUST_ORDER.map((l) => ({ value: l, label: TRUST_META[l].label }))]}
          />
          <Select
            value={unit}
            aria-label="Tipo de unidad"
            onChange={(e) => setUnit(e.target.value as "" | UnitType)}
            options={[{ value: "", label: "Cualquier unidad" }, ...(Object.keys(UNIT_LABELS) as UnitType[]).map((u) => ({ value: u, label: UNIT_LABELS[u] }))]}
          />
        </div>
      }
    >
      <Card padding="md" className="mb-4">
        <BlockTitle>Niveles de confianza</BlockTitle>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {TRUST_ORDER.map((l) => (
            <div key={l} className="flex items-start gap-2">
              <TrustBadge level={l} size="sm" />
              <p className="text-xs text-content-muted">{TRUST_META[l].hint}</p>
            </div>
          ))}
        </div>
      </Card>

      {carriers.loading && !carriers.data ? (
        <Loader />
      ) : carriers.error ? (
        <ErrorState error={carriers.error} onRetry={() => void carriers.reload()} />
      ) : rows.length === 0 ? (
        <EmptyState
          icon="ri-truck-line"
          title={carriers.data?.length ? "Ningún transportista coincide con los filtros" : "Aún no hay transportistas en el directorio"}
          description={carriers.data?.length ? "Prueba con otro nivel, tipo de unidad o búsqueda." : undefined}
        />
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {rows.map((c) => (
            <EntityCard key={c.companyId}>
              <EntityCardHeader
                icon={<i className="ri-truck-line" />}
                iconTone={TRUST_META[c.level]?.tone ?? "neutral"}
                title={c.name}
                subtitle={ratingText(c.rating, c.ratingCount)}
                actions={<TrustBadge level={c.level} size="sm" />}
              />
              <EntityCardBody>
                <EntityCardRow label="Viajes cerrados">{c.closedTrips}</EntityCardRow>
                <EntityCardRow label="Capacidad abierta">
                  {c.openCapacity > 0 ? <Badge tone="success" size="sm">{c.openCapacity} unidad(es)</Badge> : "Sin publicar"}
                </EntityCardRow>
                <EntityCardRow label="Unidades" align="start">
                  <span className="whitespace-normal">{c.unitTypes.length ? c.unitTypes.map((u) => UNIT_LABELS[u]).join(", ") : "—"}</span>
                </EntityCardRow>
                <div>
                  <p className="text-content-secondary mb-1">Rutas frecuentes</p>
                  {c.lanes.length ? (
                    <div className="flex flex-wrap gap-1">
                      {c.lanes.slice(0, 4).map((l) => <Badge key={l} tone="neutral" size="sm">{l}</Badge>)}
                      {c.lanes.length > 4 && <Badge tone="neutral" size="sm">+{c.lanes.length - 4}</Badge>}
                    </div>
                  ) : (
                    <p className="text-content-faint">Sin rutas registradas</p>
                  )}
                </div>
              </EntityCardBody>
              <EntityCardFooter><i className="ri-lock-line mr-1" />El contacto se comparte al confirmar un viaje.</EntityCardFooter>
            </EntityCard>
          ))}
        </div>
      )}
    </Section>
  );
}

// ===========================================================================
// 6. Verificación del embarcador
// ===========================================================================

interface ProfileForm {
  legalName: string;
  tradeName: string;
  rfc: string;
  contactName: string;
  contactPhone: string;
  contactEmail: string;
  carrier: boolean;
  shipper: boolean;
  shipperKind: "shipper" | "forwarder";
  creditRequested: boolean;
  frequentGeofenceIds: string[];
}

function profileToForm(p: Profile | null): ProfileForm {
  return {
    legalName: p?.legalName ?? "",
    tradeName: p?.tradeName ?? "",
    rfc: p?.rfc ?? "",
    contactName: p?.contactName ?? "",
    contactPhone: p?.contactPhone ?? "",
    contactEmail: p?.contactEmail ?? "",
    carrier: p?.roles?.carrier ?? false,
    shipper: p?.roles?.shipper ?? true,
    shipperKind: p?.shipperKind ?? "shipper",
    creditRequested: p?.credit?.requested ?? false,
    frequentGeofenceIds: p?.frequentGeofenceIds ?? [],
  };
}

const RFC_RE = /^[A-ZÑ&]{3,4}\d{6}[A-Z0-9]{3}$/;

function DocUploadModal({ docType, label, onClose, onDone }: { docType: string; label: string; onClose: () => void; onDone: () => void }) {
  const [number, setNumber] = useState("");
  const [issuedAt, setIssuedAt] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [fileUrl, setFileUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const urlError = fileUrl && !/^https?:\/\//i.test(fileUrl.trim()) ? "Debe ser un enlace que empiece con https://" : undefined;
  const dateError = issuedAt && expiresAt && expiresAt < issuedAt ? "La vigencia no puede ser anterior a la emisión" : undefined;

  const submit = async () => {
    if (!fileUrl.trim()) {
      toast.error("Agrega el enlace al documento");
      return;
    }
    if (urlError || dateError) return;
    setSaving(true);
    try {
      await exchangeApi.submitDocument({
        ownerType: "shipper",
        docType,
        number: number.trim() || undefined,
        issuedAt: issuedAt || null,
        expiresAt: expiresAt || null,
        fileUrl: fileUrl.trim(),
      });
      toast.success("Enviado a revisión");
      onDone();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={label}
      onClose={onClose}
      width="w-full max-w-xl"
      content={
        <div className="space-y-4">
          <p className="text-sm text-content-muted">El equipo de WAYAK revisa cada documento; te avisamos cuando se apruebe o si falta algo.</p>
          <Input label="Número o folio" value={number} onChange={(e) => setNumber(e.target.value)} />
          <div className="grid gap-4 sm:grid-cols-2">
            <Input type="date" label="Fecha de emisión" value={issuedAt} onChange={(e) => setIssuedAt(e.target.value)} />
            <Input type="date" label="Vigente hasta" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} error={dateError} />
          </div>
          <Input label={<>Enlace al documento (PDF)<span className="text-error"> *</span></>} value={fileUrl} onChange={(e) => setFileUrl(e.target.value)} placeholder="https://…" error={urlError} hint="Comparte un enlace de Drive, Dropbox u otro almacenamiento con acceso de lectura." />
          <div className="flex gap-3 border-t border-line pt-4">
            <Button variant="outline" size="md" shape="pill" className="flex-1" onClick={onClose}>Cancelar</Button>
            <Button size="md" shape="pill" className="flex-1" loading={saving} onClick={submit}>Enviar a revisión</Button>
          </div>
        </div>
      }
    />
  );
}

export function VerificacionEmbarcadorSection() {
  const { me, loading, error, reload } = useMe();
  const [f, setF] = useState<ProfileForm>(() => profileToForm(null));
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState<{ docType: string; label: string } | null>(null);
  const profile = me?.profile ?? null;

  useEffect(() => {
    setF(profileToForm(profile));
  }, [profile]);

  if (loading) return <Section title="Verificación"><Loader /></Section>;
  if (error || !me) return <Section title="Verificación"><ErrorState error={error || "Sin información"} onRetry={() => void reload()} /></Section>;

  const set = <K extends keyof ProfileForm>(k: K, v: ProfileForm[K]) => setF((p) => ({ ...p, [k]: v }));
  const rfc = f.rfc.trim().toUpperCase();
  const errors: Partial<Record<keyof ProfileForm, string>> = {};
  if (!f.legalName.trim()) errors.legalName = "Indica la razón social";
  if (rfc && !RFC_RE.test(rfc)) errors.rfc = "RFC inválido (12 o 13 caracteres)";
  if (!f.carrier && !f.shipper) errors.shipper = "Elige al menos un rol";
  if (f.contactEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.contactEmail.trim())) errors.contactEmail = "Correo inválido";
  const shown = (k: keyof ProfileForm) => (touched ? errors[k] : undefined);

  const save = async () => {
    setTouched(true);
    if (Object.keys(errors).length) {
      toast.error("Revisa los campos marcados");
      return;
    }
    setSaving(true);
    try {
      await exchangeApi.saveProfile({
        legalName: f.legalName.trim(),
        tradeName: f.tradeName.trim() || null,
        rfc: rfc || null,
        contactName: f.contactName.trim() || null,
        contactPhone: f.contactPhone.trim() || null,
        contactEmail: f.contactEmail.trim() || null,
        roles: { carrier: f.carrier, shipper: f.shipper },
        shipperKind: f.shipperKind,
        credit: { requested: f.creditRequested, enabled: profile?.credit?.enabled ?? false, days: profile?.credit?.days ?? null },
        frequentGeofenceIds: f.frequentGeofenceIds,
      });
      toast.success("Datos de la empresa guardados");
      await reload();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const docSpecs = Object.entries(me.catalogs.docTypes?.shipper ?? {});
  const shipperDocs = me.documents.filter((d) => d.ownerType === "shipper");
  const latest = (docType: string) => shipperDocs.find((d) => d.docType === docType) ?? null; // vienen del más nuevo al más viejo
  const today = new Date().toISOString().slice(0, 10);
  const statusOf = (docType: string): keyof typeof DOC_STATUS_META => {
    const d = latest(docType);
    if (!d) return "missing";
    if (d.status === "approved" && d.expiresAt && d.expiresAt.slice(0, 10) < today) return "expired";
    return d.status;
  };
  const required = docSpecs.filter(([, s]) => s.required);
  const requiredApproved = required.filter(([k]) => statusOf(k) === "approved").length;
  const geofences = me.fleet?.geofences ?? [];
  const toggleGeofence = (id: string, on: boolean) =>
    set("frequentGeofenceIds", on ? [...new Set([...f.frequentGeofenceIds, id])] : f.frequentGeofenceIds.filter((x) => x !== id));

  return (
    <Section title="Verificación" subtitle="Datos de tu empresa y expediente. Verificamos a los embarcadores para que el transportista sepa que le van a pagar.">
      <SandboxHint />
      <div className="max-w-4xl mx-auto space-y-4">
        <Card padding="md">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-xs text-content-muted">Tu nivel como embarcador</p>
              <div className="flex flex-wrap items-center gap-2 mt-1">
                <TrustBadge level={me.shipper.level} />
                {me.shipper.canOperate ? <Badge tone="success"><i className="ri-check-line mr-1" />Puede publicar cargas</Badge> : <Badge tone="warning">Aún no puede publicar</Badge>}
                {profile?.credit?.enabled && <Badge tone="info">Crédito a {profile.credit.days ?? "—"} días</Badge>}
              </div>
              <p className="text-xs text-content-muted mt-2">
                Documentos obligatorios aprobados: <b className="text-content">{requiredApproved} de {required.length}</b>
                {me.shipper.pending.length > 0 && ` · ${me.shipper.pending.length} en revisión`}
              </p>
            </div>
          </div>
          {me.shipper.expiring.length > 0 && (
            <ul className="mt-3 space-y-1">
              {me.shipper.expiring.map((x) => (
                <li key={x.docType} className="text-xs text-warning"><i className="ri-alarm-warning-line mr-1" />{x.label} vence en {x.daysLeft} días ({x.expiresAt.slice(0, 10)})</li>
              ))}
            </ul>
          )}
        </Card>

        <FormBlock title="Datos de la empresa" icon="ri-building-line">
          <div className="grid gap-4 sm:grid-cols-2">
            <Input label={<>Razón social<span className="text-error"> *</span></>} value={f.legalName} onChange={(e) => set("legalName", e.target.value)} error={shown("legalName")} />
            <Input label="Nombre comercial" value={f.tradeName} onChange={(e) => set("tradeName", e.target.value)} hint="Es el que ven los transportistas." />
            <Input label="RFC" value={f.rfc} onChange={(e) => set("rfc", e.target.value.toUpperCase())} error={shown("rfc")} maxLength={13} />
            <Select
              label="Tipo de embarcador"
              value={f.shipperKind}
              onChange={(e) => set("shipperKind", e.target.value as "shipper" | "forwarder")}
              options={[
                { value: "shipper", label: "Dueño de la carga (embarcador directo)" },
                { value: "forwarder", label: "Agente de carga (forwarder / 3PL)" },
              ]}
            />
            <Input label="Nombre de contacto" value={f.contactName} onChange={(e) => set("contactName", e.target.value)} />
            <Input type="tel" label="Teléfono de contacto" value={f.contactPhone} onChange={(e) => set("contactPhone", e.target.value)} />
            <Input type="email" label="Correo de contacto" value={f.contactEmail} onChange={(e) => set("contactEmail", e.target.value)} error={shown("contactEmail")} containerClassName="sm:col-span-2" hint="El contacto solo se comparte con el transportista al confirmar un viaje." />
          </div>

          <div className="mt-5 space-y-3">
            <p className="text-sm font-medium text-content">Cómo operas en Cargo Market<span className="text-error"> *</span></p>
            <div className="grid gap-3 sm:grid-cols-2">
              <Check checked={f.shipper} onChange={(v) => set("shipper", v)} label="Embarcador" hint="Publico cargas y contrato transporte." />
              <Check checked={f.carrier} onChange={(v) => set("carrier", v)} label="Transportista" hint="Tengo flotilla y tomo cargas." />
            </div>
            {shown("shipper") && <p className="text-error text-xs">{shown("shipper")}</p>}
          </div>

          <div className="mt-5 rounded-xl border border-line bg-app p-4">
            <Check
              checked={f.creditRequested}
              onChange={(v) => set("creditRequested", v)}
              label="Solicitar línea de crédito (30/60 días)"
              hint="Sin crédito, el flete se paga por adelantado y queda retenido (escrow) hasta la entrega. WAYAK autoriza el crédito tras revisar tu historial y buró."
            />
            {profile?.credit?.requested && !profile.credit.enabled && <Badge tone="warning" className="mt-2">Solicitud en revisión</Badge>}
          </div>

          <div className="mt-5">
            <p className="text-sm font-medium text-content">Direcciones frecuentes (geocercas)</p>
            <p className="text-xs text-content-muted mb-2">Elige las geocercas de tu flotilla donde sueles cargar o descargar; aparecerán al publicar.</p>
            {geofences.length === 0 ? (
              <p className="text-sm text-content-faint">No tienes geocercas en tu flotilla.</p>
            ) : (
              <div className="grid gap-2 sm:grid-cols-2 max-h-60 overflow-y-auto rounded-xl border border-line bg-app p-3">
                {geofences.map((g) => (
                  <Check key={g.id} checked={f.frequentGeofenceIds.includes(g.id)} onChange={(v) => toggleGeofence(g.id, v)} label={g.name} hint={g.address || undefined} />
                ))}
              </div>
            )}
          </div>

          <div className="flex gap-3 border-t border-line pt-4 mt-5">
            <Button variant="outline" size="md" shape="pill" className="flex-1" disabled={saving} onClick={() => { setF(profileToForm(profile)); setTouched(false); }}>Descartar cambios</Button>
            <Button size="md" shape="pill" className="flex-1" loading={saving} onClick={save}>{profile ? "Guardar cambios" : "Dar de alta mi empresa"}</Button>
          </div>
        </FormBlock>

        <FormBlock title="Expediente del embarcador" icon="ri-file-shield-2-line" subtitle="Los obligatorios habilitan el nivel Verificado; los opcionales suman insignias.">
          {!profile ? (
            <EmptyState icon="ri-building-line" title="Primero guarda los datos de tu empresa" description="Después podrás subir tu expediente." />
          ) : docSpecs.length === 0 ? (
            <EmptyState icon="ri-file-list-3-line" title="Sin documentos configurados" />
          ) : (
            <ul className="divide-y divide-line">
              {docSpecs.map(([docType, spec]) => {
                const st = statusOf(docType);
                const meta = DOC_STATUS_META[st];
                const d = latest(docType);
                return (
                  <li key={docType} className="py-3 flex flex-col sm:flex-row sm:items-center gap-3">
                    <span className={cn("w-9 h-9 rounded-xl flex items-center justify-center shrink-0", TONE_TILE[meta.tone])}>
                      <i className={meta.icon} />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-sm font-medium text-content">{spec.label}</p>
                        {spec.required ? <Badge tone="primary" size="sm">Obligatorio</Badge> : <Badge tone="neutral" size="sm">Opcional{spec.badge ? " · insignia" : ""}</Badge>}
                        <Badge tone={meta.tone} size="sm">{meta.label}</Badge>
                      </div>
                      <p className="text-xs text-content-muted mt-0.5">
                        {d
                          ? [d.number && `Folio ${d.number}`, d.expiresAt && `vigente hasta ${d.expiresAt.slice(0, 10)}`, spec.validDays && `se renueva cada ${spec.validDays} días`].filter(Boolean).join(" · ") || "Enviado"
                          : spec.validDays
                            ? `Vigencia de ${spec.validDays} días`
                            : "Aún no lo envías"}
                      </p>
                      {d?.status === "rejected" && d.reviewNote && <p className="text-xs text-error mt-0.5">Motivo: {d.reviewNote}</p>}
                    </div>
                    <div className="flex gap-2 shrink-0">
                      {d?.fileUrl && (
                        <Button size="sm" variant="ghost" iconOnly aria-label="Abrir documento" icon={<i className="ri-external-link-line" />} onClick={() => window.open(d.fileUrl as string, "_blank", "noopener")} />
                      )}
                      <Button size="sm" variant={st === "approved" ? "outline" : "primary"} icon={<i className="ri-upload-2-line" />} onClick={() => setUploading({ docType, label: spec.label })}>
                        {st === "missing" ? "Subir" : "Actualizar"}
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </FormBlock>
      </div>

      {uploading && (
        <DocUploadModal
          docType={uploading.docType}
          label={uploading.label}
          onClose={() => setUploading(null)}
          onDone={() => {
            setUploading(null);
            void reload();
          }}
        />
      )}
    </Section>
  );
}
